import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  OnChanges,
  Output,
  OnDestroy,
  EventEmitter,
} from "@angular/core"
import { FormGroup, Validator, Validators } from "@angular/forms"
import countries from "countries-phone-masks"
import { PhoneNumberUtil } from "google-libphonenumber"

import { MigrationStatus } from "../../enum/migrationStatus"

import { Subject } from "rxjs"
import { debounceTime } from "rxjs/operators"

interface ICountryOption {
  code: string
  flag: string
  iso: string
  name: string
  mask: string | string[]
}

export interface IIntlInitialValue {
  ddi: string
  phone: string
}

const MASK_FORMAT_REGEX = /#/g

const BRAZIL_DIAL_CODE = "+55"

const INVALID_FORMAT_MESSAGE = "Preencha no formato válido."

@Component({
  selector: "intl-input",
  templateUrl: "./intl-input.component.html",
  styleUrls: ["./intl-input.component.css"],
})
export class IntlInputComponent implements OnChanges, OnDestroy {
  @ViewChild("phoneInput") phoneInput!: ElementRef<HTMLInputElement>
  @ViewChild("countriesOptionRef")
  countriesOptionRef!: ElementRef<HTMLDivElement>
  @Input() name: string
  @Input() label: string
  @Input() formGroup: FormGroup
  @Input() disabled = false
  @Input() initialValue?: IIntlInitialValue
  @Input() migrationStatus: MigrationStatus
  @Input() required = false
  @Input() initialIso: string
  @Input() tabindex: number
  @Output() onChange: EventEmitter<IIntlInitialValue> = new EventEmitter()
  @Output() onInvalidDdi: EventEmitter<string> = new EventEmitter()
  private phoneValueChange: Subject<string> = new Subject<string>()
  selectedCountry: ICountryOption
  countriesOptions: ICountryOption[]
  countriesDropdownStatus = false
  dropdownIcon = "keyboard_arrow_down_icon"
  phonePlaceholder = "(00) 00000-0000"
  phoneUtil: any

  constructor() {
    this.phoneUtil = PhoneNumberUtil.getInstance()

    this.countriesOptions = this.handleSortCountries(
      countries as ICountryOption[]
    )

    // Initialize with Brazil as default to prevent undefined errors
    this.selectedCountry = this.getDefaultCountry()

    window.onkeydown = (e) => this.onSearchOptionThatStart(e)
  }
  ngOnInit() {
    this.phoneValueChange.pipe(debounceTime(600)).subscribe((value) => {
      this.validatePhoneValue(value)
    })
  }
  ngOnChanges() {
    if (this.initialIso) {
      const foundCountry = this.countriesOptions.find(
        (countryOption) => countryOption.iso === this.initialIso
      )
      if (!foundCountry) {
        // Fallback to Brazil if ISO not found
        this.selectedCountry = this.getDefaultCountry()
        this.initialIso = null
        this.onChange.emit({ ddi: this.selectedCountry.code, phone: "" })
        return
      }
      this.selectedCountry = foundCountry
      this.initialValue = {
        ddi: this.selectedCountry.code,
        phone: "",
      }
      this.initialIso = null
      this.onChange.emit({ ddi: this.initialValue.ddi, phone: "" })
      this.onSelectCountry(this.selectedCountry)
    }
    if (!this.initialValue || !this.initialValue.ddi) {
      this.selectedCountry = this.getDefaultCountry()
      this.onChange.emit({ ddi: this.selectedCountry.code, phone: "" })
      return
    }
    const { phone, ddi } = this.initialValue

    const foundCountry = this.countriesOptions.find(
      (countryOption) => countryOption.code === ddi
    )

    if (!foundCountry) {
      this.onInvalidDdi.emit(ddi)
      this.selectedCountry = this.getDefaultCountry()
      this.resetField()
      this.onChange.emit({ ddi: this.selectedCountry.code, phone: "" })
      return
    }

    this.selectedCountry = foundCountry

    if (!phone) return

    const number = this.phoneUtil.parseAndKeepRawInput(
      phone,
      this.selectedCountry.iso
    )

    this.formGroup.patchValue({
      [this.name]: this.phoneUtil.formatInOriginalFormat(
        number,
        this.selectedCountry.iso
      ),
    })
  }

  onSelectCountry(country: ICountryOption) {
    if (this.disabled || !country) return

    const { mask } = country

    this.resetField()
    this.phonePlaceholder = this.handleFormatMask(mask)
    this.selectedCountry = country
    this.onCloseDropdown()
  }

  onOpenDropdown() {
    if (this.disabled) return

    this.countriesDropdownStatus = true
    this.dropdownIcon = "keyboard_arrow_up_icon"
  }

  onCloseDropdown() {
    if (this.disabled) return

    this.countriesDropdownStatus = false
    this.dropdownIcon = "keyboard_arrow_down_icon"
  }

  validateCellPhone(phone: number, ddi: number): boolean {
    if (phone) {
      if (+ddi == 55) {
        const cellPhoneRegex: RegExp = /^\d{2}9\d{8}$/
        return cellPhoneRegex.test(phone.toString())
      }
    }
    return true
  }
  onChangePhoneValue(value: string) {
    this.phoneValueChange.next(value)
  }
  validatePhoneValue(value: string) {
    try {
      if (!this.selectedCountry) {
        this.selectedCountry = this.getDefaultCountry()
      }
      const { iso, code } = this.selectedCountry
      this.formGroup.setErrors({})
      if (!value && !this.required) {
        this.formGroup.controls[this.name].valid
        this.formGroup.setErrors({})
        this.formGroup.updateValueAndValidity()
        this.onChange.emit({ phone: "", ddi: code })
        return
      }
      if (this.disabled) return

      const number = this.phoneUtil.parseAndKeepRawInput(value, iso)
      const ddi = number.values_[1]
      const phone = number.values_[2]
      const isValidCellPhone = this.validateCellPhone(phone, ddi)
      const isAValidNumber = this.phoneUtil.isValidNumberForRegion(number, iso)

      if (!isAValidNumber || !isValidCellPhone) {
        this.formGroup.controls[this.name].invalid
        this.formGroup
          .get(this.name)
          .setErrors({ "Preencha no formato válido.": true })
        return
      }
      this.phoneInput.nativeElement.value =
        this.phoneUtil.formatInOriginalFormat(number, iso)
      this.formGroup.controls[this.name].valid
      this.formGroup.setErrors({})
      this.formGroup.updateValueAndValidity()
      this.onChange.emit({ phone: value, ddi: code })
    } catch (error) {
      if (!this.required && !value.length) {
        this.formGroup.setErrors({ [this.name]: INVALID_FORMAT_MESSAGE })
        return
      }
      this.formGroup.setErrors({ [this.name]: INVALID_FORMAT_MESSAGE })
    }
  }

  onSearchOptionThatStart(event) {
    if (typeof window === "undefined") return
    if (!this.countriesOptionRef) return

    const { key } = event

    const findedCountry = this.countriesOptions.find((country) =>
      country.code.replace("+", "").startsWith(key)
    )

    if (!findedCountry) return

    const optionHTML = document.getElementById(`${findedCountry.code}`)

    this.countriesOptionRef.nativeElement.scrollTo({
      top: optionHTML.offsetTop,
    })
  }

  ngOnDestroy() {
    window.onkeydown = null
  }

  private handleSortCountries(countries: ICountryOption[]) {
    return countries
      .filter(
        (countryData) =>
          countryData.code !== "+1" ||
          countryData.iso === "US" ||
          countryData.iso === "CA"
      )
      .sort((countryA, countryB) => {
        if (countryA.code === BRAZIL_DIAL_CODE) return -1
        if (countryB.code === BRAZIL_DIAL_CODE) return 1
        if (countryA.code > countryB.code) return 1
        return -1
      })
  }

  private handleFormatMask(mask: string | string[]) {
    if (typeof mask === "string") {
      return mask.replace(MASK_FORMAT_REGEX, "0")
    }
    return mask[0].replace(MASK_FORMAT_REGEX, "0")
  }

  private resetField() {
    this.phoneInput.nativeElement.value = ""
    this.formGroup.patchValue({ [this.name]: "" })
    this.formGroup.setErrors(null)
  }

  get hasError() {
    return this.formGroup.getError(this.name)
  }

  checkHasError(fieldName: string) {
    const totalErrors = this.getErrorMessages(fieldName)
    return totalErrors.length > 0 ? true : false
  }

  getErrorMessages(fieldName: string): string[] {
    const control = this.formGroup.get(fieldName)
    if (control.errors) {
      return Object.keys(control.errors).map((key) => {
        return this.getErrorMessage(key)
      })
    }
    return []
  }

  getErrorMessage(errorCode: string): string {
    return `${errorCode}`
  }

  /**
   * Returns the default country (Brazil) as a fallback.
   * This ensures selectedCountry is never undefined.
   */
  private getDefaultCountry(): ICountryOption {
    const brazil = this.countriesOptions.find(
      (countryOption) => countryOption.code === BRAZIL_DIAL_CODE
    )

    // Ultimate fallback: if Brazil somehow doesn't exist, return the first country
    if (!brazil && this.countriesOptions.length > 0) {
      console.warn('Brazil not found in countries list, using first available country')
      return this.countriesOptions[0]
    }

    return brazil || {
      code: BRAZIL_DIAL_CODE,
      flag: '',
      iso: 'BR',
      name: 'Brazil',
      mask: '(##) #####-####'
    }
  }
}
